import type { ColumnType, ColumnsType } from 'antd/es/table';
import { Avatar, Flex, Typography } from 'antd';
import type { GlobalToken } from 'antd/es/theme/interface';
import { formatCurrency } from '@/utils/currencyUtils';
import { formatNumber, formatPercentage } from '@/utils/numberFormattingUtils';
import type { NetworkShow } from '@/types/api/network';
import type { TableData, TableHeader } from '@/types';

const REVENUE_KEYS = [
  'ads',
  'programmatic',
  'sponsorships',
  'branded_content',
  'social',
  'other',
] as const;

type RevenueKey = (typeof REVENUE_KEYS)[number];
const isRevenueKey = (id: string): id is RevenueKey => REVENUE_KEYS.some((key) => key === id);

/**
 * Ant Design columns for show performance: rich cells from `NetworkShow`, sort keys aligned with synthetic `TableData` for `sortData`.
 */
export const buildShowPerformanceEchoColumns = (
  headerData: TableHeader[],
  showById: Map<string, NetworkShow>,
  token: GlobalToken
): ColumnsType<TableData> => {
  return headerData.map((header): ColumnType<TableData> => {
    const align = header.styleOptions?.align === 'right' ? 'right' : 'left';
    const minWidth = header.styleOptions?.minWidth ?? 0;

    const column: ColumnType<TableData> = {
      key: header.id,
      title: header.label,
      align,
      className: header.className,
      sorter: header.sortable ?? false,
      minWidth,
      onHeaderCell: () => ({ style: { color: token.colorTextSecondary } }),
      render: (_value, record) => {
        const show = showById.get(record.id);
        if (!show) {
          return null;
        }

        const { image_url, name, impressions, revenue, percentage } = show;

        switch (header.id) {
          case 'name':
            return (
              <Flex align="center" gap={16} style={{ width: '100%', minWidth: 0 }}>
                <Avatar
                  alt={`${name} Thumbnail`}
                  size={40}
                  shape="square"
                  src={image_url ?? undefined}
                  data-testid="show-thumbnail"
                  style={{ flexShrink: 0 }}
                />
                <Typography.Text ellipsis style={{ flex: 1, minWidth: 0 }}>
                  {name}
                </Typography.Text>
              </Flex>
            );
          case 'impressions':
            return formatNumber('en', impressions || 0);
          case 'total':
            return (
              <Typography.Text strong>
                {formatCurrency(revenue.total.amount, revenue.total.currency)}
              </Typography.Text>
            );
          case 'percentage':
            return (
              <Typography.Text type="secondary" data-testid={`percentage-value-${record.id}`}>
                {`${formatPercentage(percentage, { round: false, decimals: 2 })}%`}
              </Typography.Text>
            );
          default:
            if (isRevenueKey(header.id)) {
              return (
                <Typography.Text className="font-light">
                  {formatCurrency(revenue[header.id].amount, revenue[header.id].currency)}
                </Typography.Text>
              );
            }
            return null;
        }
      },
    };

    return column;
  });
};
